<template>
  <div class="sidebar-pinned">
    <Navbar />

    <main class="admin-main">

      <!-- ── Page Header Slider ───────────────────────────────────── -->
      <div class="dash-header">

        <!-- Slide backgrounds (stacked, CSS cross-fades) -->
        <div
          v-for="(slide, i) in slides"
          :key="i"
          class="dash-slide-bg"
          :class="{ active: i === currentSlide, prev: i === prevSlide }"
          :style="{ backgroundImage: `url(${slide.image})` }"
        ></div>

        <!-- Overlays -->
        <div class="dash-vignette"></div>
        <div class="dash-overlay"></div>

        <!-- Content -->
        <div class="dash-header-content container">

          <!-- Agency logo + name -->
          <div class="dash-logo-row" v-if="agency">
            <div class="dash-logo-bg" v-if="agency.image_url">
              <img :src="agency.image_url" class="dash-logo-img"
                onerror="this.onerror=null;this.src='/claimify_logo.png'" />
            </div>
            <span class="dash-agency-name">{{ agency.agency_name }}</span>
          </div>

          <!-- Welcome text (transitions per slide) -->
          <transition name="text-up" mode="out-in">
            <div class="dash-welcome" :key="currentSlide">
              <div class="dash-tag">{{ slides[currentSlide].tag }}</div>
              <h3 class="dash-title">Welcome back, {{ user && user.firstname }}</h3>
              <p class="dash-desc">{{ slides[currentSlide].desc }}</p>
            </div>
          </transition>

          <!-- Footer: progress bar + dots -->
          <div class="dash-footer">
            <div class="dash-progress-track">
              <div class="dash-progress-fill" :key="currentSlide"></div>
            </div>
            <div class="dash-dots">
              <span
                v-for="(s, i) in slides"
                :key="i"
                :class="['dash-dot', { active: i === currentSlide }]"
                @click="goToSlide(i)"
              ></span>
            </div>
          </div>

        </div>
      </div>

      <div class="container mt-6  pb-5">

        <!-- ── Sector Toggle ──────────────────────────────────────── -->
        <div class="card action-card shadow mb-4">
          <div class="card-body py-3 px-4 d-flex align-items-center" style="gap: 8px;">
            <button type="button" :class="buttoncolor.formal" @click="showFormal">
              <i class="fe fe-list mr-1"></i> Formal
            </button>
            <button type="button" :class="buttoncolor.informal" @click="showInformal">
              <i class="fe fe-grid mr-1"></i> Informal
            </button>
            <router-link to="/dashboard-slideshow" class="btn btn-dark btn-sm ml-auto">
              <i class="fe fe-monitor mr-1"></i> Presentation Mode
            </router-link>
            <ReportActions />
          </div>
        </div>

        <!-- ── Sector Panels ──────────────────────────────────────── -->
        <div v-show="formal_S" class="mb-4">
          <FormalSector :key="'formal-' + chartRefreshKey" />
        </div>

        <div v-show="informal_S" class="mb-4">
          <InformalSector :key="'informal-' + chartRefreshKey" />
        </div>

        <!-- ── Turnaround times (agency staff) ─────────────────────── -->
        <div v-if="isAgencyStaff" class="mb-4">
          <TurnaroundPanel compact />
        </div>

        <!-- ── Enrolment analytics (agency staff; loaded together through /batch) ── -->
        <template v-if="isAgencyStaff">
          <EnrollmentTrends />
          <div class="row">
            <div class="col-xl-6"><SectorDistribution /></div>
            <div class="col-xl-6"><ForecastPanel fixed-metric="enrollment" compact /></div>
          </div>
          <EnrolleeMap />
          <RenewalsPanel />
        </template>

        <!-- ── Charts ─────────────────────────────────────────────── -->
        <div class="mb-4">
          <EncounterbyLGA :key="'encbylga-' + chartRefreshKey" />
        </div>

        <div class="mb-4">
          <EncounterbyDiagnosis :key="'encbydx-' + chartRefreshKey" />
        </div>

        <div class="mb-4">
          <ClaimsDate :key="'claimsdate-' + chartRefreshKey" />
        </div>

        <div class="mb-4">
          <EncountersbyDate :key="'encdate-' + chartRefreshKey" />
        </div>

        <div class="row">
          <div class="col-md-4 mb-4">
            <ClaimsCategory :key="'claimscat-' + chartRefreshKey" />
          </div>
          <div class="col-md-4 mb-4">
            <ReferralCategory :key="'refcat-' + chartRefreshKey" />
          </div>
          <div class="col-md-4 mb-4">
            <ChangeProviderCategory :key="'chprovcat-' + chartRefreshKey" />
          </div>
        </div>

      </div>

      <Footer />
    </main>
  </div>
</template>

<style scoped>
/* ── Slider Header ─────────────────────────────────────────── */
.dash-header {
  position: relative;
  height: 290px;
  overflow: hidden;
}

/* All slides stacked */
.dash-slide-bg {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  opacity: 0;
  transition: opacity 1.4s ease;
  will-change: opacity, transform;
}
.dash-slide-bg.active {
  opacity: 1;
  animation: dash-ken-burns 6s ease-out forwards;
}
.dash-slide-bg.prev {
  opacity: 0;
  transition: opacity 1.4s ease;
}
@keyframes dash-ken-burns {
  0%   { transform: scale(1);    }
  100% { transform: scale(1.07); }
}

/* Bottom vignette */
.dash-vignette {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 55%);
  z-index: 1;
}
/* Blue-toned overlay */
.dash-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    155deg,
    rgba(10, 22, 50, 0.65) 0%,
    rgba(30, 58, 95, 0.45) 60%,
    rgba(37, 99, 235, 0.2) 100%
  );
  z-index: 1;
}

/* Content layer */
.dash-header-content {
  position: relative;
  z-index: 2;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding-bottom: 24px;
  color: #fff;
}

/* Agency row top-left */
.dash-logo-row {
  position: absolute;
  top: 20px;
  left: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}
.dash-logo-bg {
  background: rgba(255,255,255,0.9);
  border-radius: 12px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 20px rgba(0,0,0,0.35);
}
.dash-logo-img {
  height: 50px;
  width: auto;
  display: block;
}
.dash-agency-name {
  font-size: 1.05rem;
  font-weight: 800;
  opacity: 1;
  letter-spacing: .02em;
  text-shadow: 0 1px 6px rgba(0,0,0,0.5);
}

/* Welcome text */
.dash-welcome { margin-bottom: 16px; }

.dash-tag {
  display: inline-block;
  font-size: .68rem;
  font-weight: 700;
  letter-spacing: .11em;
  text-transform: uppercase;
  background: rgba(255,255,255,0.15);
  border: 1px solid rgba(255,255,255,0.3);
  border-radius: 20px;
  padding: 2px 10px;
  margin-bottom: 8px;
  backdrop-filter: blur(4px);
}

.dash-title {
  font-size: 2.2rem;
  font-weight: 900;
  line-height: 1.15;
  letter-spacing: -0.02em;
  margin-bottom: 6px;
  text-shadow: 0 2px 12px rgba(0,0,0,0.4);
}

.dash-desc {
  font-size: 1rem;
  font-weight: 500;
  opacity: .95;
  margin: 0;
  max-width: 500px;
  line-height: 1.6;
}

/* Footer: progress + dots */
.dash-footer {
  display: flex;
  align-items: center;
  gap: 14px;
}
.dash-progress-track {
  flex: 1;
  height: 2px;
  background: rgba(255,255,255,0.2);
  border-radius: 2px;
  overflow: hidden;
}
.dash-progress-fill {
  height: 100%;
  width: 0%;
  background: rgba(255,255,255,0.85);
  border-radius: 2px;
  animation: dash-fill 5.5s linear forwards;
}
@keyframes dash-fill {
  from { width: 0%; }
  to   { width: 100%; }
}
.dash-dots {
  display: flex;
  gap: 6px;
  align-items: center;
}
.dash-dot {
  height: 5px;
  width: 5px;
  border-radius: 3px;
  background: rgba(255,255,255,0.35);
  cursor: pointer;
  transition: all 0.4s cubic-bezier(.4,0,.2,1);
}
.dash-dot.active {
  width: 24px;
  background: #fff;
}

/* ── Text transitions ──────────────────────────────────────── */
.text-up-enter-active { transition: opacity .7s ease .15s, transform .7s ease .15s; }
.text-up-leave-active { transition: opacity .3s ease, transform .3s ease; }
.text-up-enter        { opacity: 0; transform: translateY(18px); }
.text-up-leave-to     { opacity: 0; transform: translateY(-10px); }

/* ── Sector toggle card ────────────────────────────────────── */
.action-card {
  border-radius: 12px;
  border: 1px solid #e9ecef;
  margin-top: -20px;
}
</style>

<script>
import Navbar from "@/views/Navbar.vue";
import ForecastPanel from "@/views/analytics/components/ForecastPanel.vue";
import RenewalsPanel from "@/views/analytics/components/RenewalsPanel.vue";
import EnrolleeMap from "@/views/analytics/components/EnrolleeMap.vue";
import SectorDistribution from "@/views/analytics/components/SectorDistribution.vue";
import EnrollmentTrends from "@/views/analytics/components/EnrollmentTrends.vue";
import TurnaroundPanel from "@/views/reports/components/TurnaroundPanel.vue";
import ReportActions from "@/views/reports/components/ReportActions.vue";
import Footer from "@/views/Footer.vue";
import InformalSector from "@/views/shis/components/InformalSector.vue";
import FormalSector from "@/views/shis/components/FormalSector.vue";
import EncounterbyDiagnosis from "@/views/shis/components/EncounterbyDiagnosis.vue";
import EncounterbyLGA from "@/views/shis/components/EncounterbyLGA";
import ClaimsDate from "@/views/shis/components/ClaimsbyDate";
import EncountersbyDate from "@/views/shis/components/EncountersbyDate";
import ClaimsCategory from "@/views/shis/components/ClaimStatusChart";
import ReferralCategory from "@/views/shis/components/ReferralStatusChart";
import ChangeProviderCategory from "@/views/shis/components/ChangeProviderStatusChart";

let agency = JSON.parse(localStorage.getItem("agency"));

export default {
  components: { ReportActions, ForecastPanel, RenewalsPanel, EnrolleeMap, SectorDistribution, EnrollmentTrends, TurnaroundPanel,
    EncounterbyDiagnosis,
    Navbar,
    InformalSector,
    FormalSector,
    Footer,
    ClaimsDate,
    EncountersbyDate,
    ClaimsCategory,
    ReferralCategory,
    ChangeProviderCategory,
    EncounterbyLGA,
  },

  data() {
    return {
      isAgencyStaff: ["shis", "employee"].includes((JSON.parse(localStorage.getItem("user") || "{}") || {}).type),
      agency: agency,
      user: null,
      formal_S: true,
      informal_S: false,
      mydata: "",
      auth_user: "",
      providers: "",
      clients: "",
      total_clients: "",
      trial_date: "11/11/2025",
      claims: "",
      offlineclients: [],
      employees: "",
      plans: "",
      tpas: "",
      buttoncolor: {
        informal: "btn btn-outline-secondary",
        formal: "btn btn-submit",
      },
      currentSlide: 0,
      prevSlide: null,
      slideTimer: null,
      slideImages: [
        "/background/login1.jpg",
        "/background/login2.jpg",
        "/background/login3.jpg",
      ],
      chartRefreshKey: 0,
      chartRefreshTimer: null,
    };
  },

  beforeMount() {
    this.user = JSON.parse(localStorage.getItem("user"));
    this.axios
      .get(`/api/v1/auth/user`)
      .then((response) => { this.auth_user = response.data; })
      .catch((error) => { console.error(error); });
  },

  mounted() {
    this.startTimer();
    this.loadSlideImages(this.agency && this.agency.id);
    // Every chart below fetches its own data once on mount; bumping this key
    // remounts them so they refetch, keeping the dashboard in sync with the
    // backend without needing a manual page reload.
    this.chartRefreshTimer = setInterval(() => {
      this.chartRefreshKey++;
    }, 3 * 60 * 1000);
  },

  beforeDestroy() {
    clearInterval(this.slideTimer);
    clearInterval(this.chartRefreshTimer);
  },

  computed: {
    slides() {
      const texts = [
        { tag: "State Health Insurance",    desc: "Connecting enrollees with trusted healthcare providers across the state." },
        { tag: "Universal Health Coverage", desc: "Building healthier communities through comprehensive health insurance coverage." },
        { tag: "Claims Management",         desc: "Fast, transparent, and reliable health insurance claims processing." },
      ];
      return this.slideImages.map((img, i) => ({
        image: img,
        ...texts[i % texts.length],
      }));
    },
  },

  methods: {
    async loadSlideImages(agencyId) {
      if (String(agencyId) !== '313923') return; // only custom backgrounds for this agency
      const folder = `/background/313923`;
      try {
        const resp = await fetch(`${folder}/manifest.json`);
        if (!resp.ok) return;
        const files = await resp.json();
        if (Array.isArray(files) && files.length) {
          this.slideImages = files.map(f => `${folder}/${f}`);
        }
      } catch {
        // keep defaults
      }
    },
    startTimer() {
      this.slideTimer = setInterval(() => {
        this.prevSlide = this.currentSlide;
        this.currentSlide = (this.currentSlide + 1) % this.slides.length;
      }, 5500);
    },
    goToSlide(i) {
      this.prevSlide = this.currentSlide;
      this.currentSlide = i;
      clearInterval(this.slideTimer);
      this.startTimer();
    },
    showFormal() {
      this.formal_S = true;
      this.informal_S = false;
      this.buttoncolor.formal = "btn btn-submit";
      this.buttoncolor.informal = "btn btn-outline-secondary";
    },
    showInformal() {
      this.formal_S = false;
      this.informal_S = true;
      this.buttoncolor.informal = "btn btn-submit";
      this.buttoncolor.formal = "btn btn-outline-secondary";
    },
  },
  created() {},
};
</script>
